import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  Alert, AlertDescription, Badge, Button, Checkbox, Combobox, Dialog, DialogContent,
  DialogFooter, DialogHeader, DialogTitle, Input, Label, Skeleton,
} from '@/components/cargofl-ui';
import { api, ApiError, toastFailure } from '@/lib/api';
import { usePeople } from '@/lib/people-api';

/**
 * The rates on one customer, project or activity.
 *
 * A dialog rather than a page, because a rate is an attribute of the thing it
 * hangs off and is edited while looking at it - not somewhere else.
 *
 * The precedence note at the bottom is not decoration. It is genuinely
 * surprising - a generic activity rate outranks a personal project rate - and
 * somebody setting a rate that then fails to apply will otherwise conclude the
 * feature is broken rather than that it is working as the old system did.
 */
export type RateKind = 'customers' | 'projects' | 'activities';

interface RateRow {
  id: string;
  employeeId: string | null;
  rateMinor: string;
  internalRateMinor: string | null;
  isFixed: boolean;
  employee: { id: string; displayName: string; alias: string | null } | null;
}

/** Minor units as a string to a plain decimal, without touching a float. */
function toDecimal(minor: string): string {
  const negative = minor.startsWith('-');
  const digits = (negative ? minor.slice(1) : minor).padStart(3, '0');
  return `${negative ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

const LABEL: Record<RateKind, string> = {
  customers: 'customer',
  projects: 'project',
  activities: 'activity',
};

export function RatesDialog({
  kind,
  id,
  name,
  currency,
  canEdit,
  budget,
  onClose,
}: {
  kind: RateKind;
  id: string | null;
  name: string;
  /** Shown beside every amount, so nobody has to assume which currency. */
  currency: string;
  canEdit: boolean;
  /** Current budgets, so the fields open with what is already set. */
  budget?: { budgetMinor?: string; timeBudgetSeconds?: number; budgetType?: string | null };
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: people } = usePeople(false);
  const [employeeId, setEmployeeId] = React.useState('');
  const [rate, setRate] = React.useState('');
  const [internalRate, setInternalRate] = React.useState('');
  const [isFixed, setIsFixed] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [budgetAmount, setBudgetAmount] = React.useState(
    budget?.budgetMinor && budget.budgetMinor !== '0' ? toDecimal(budget.budgetMinor) : '',
  );
  const [budgetHours, setBudgetHours] = React.useState(
    budget?.timeBudgetSeconds ? String(budget.timeBudgetSeconds / 3600) : '',
  );
  const [budgetMonthly, setBudgetMonthly] = React.useState(budget?.budgetType === 'month');
  const [budgetSaved, setBudgetSaved] = React.useState(false);

  const { data: rows, isPending } = useQuery({
    queryKey: ['catalog', kind, id, 'rates'],
    queryFn: () => api.get<RateRow[]>(`/catalog/${kind}/${id}/rates`),
    enabled: id !== null,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['catalog', kind, id, 'rates'] });
  };

  const save = useMutation({
    mutationFn: (body: unknown) => api.post(`/catalog/${kind}/${id}/rates`, body),
    onSuccess: () => {
      setRate('');
      setInternalRate('');
      setError(null);
      invalidate();
    },
    onError: (e) => setError(`Rate not saved: ${e instanceof ApiError ? e.message : 'something went wrong.'}`),
  });

  const remove = useMutation({
    mutationFn: (rateId: string) => api.delete(`/catalog/${kind}/rates/${rateId}`),
    onSuccess: invalidate,
    onError: toastFailure('Could not remove that rate'),
  });

  const saveBudget = useMutation({
    mutationFn: (body: unknown) => api.post(`/catalog/${kind}/${id}/budget`, body),
    onSuccess: () => {
      setBudgetSaved(true);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: (e) => setError(`Budget not saved: ${e instanceof ApiError ? e.message : 'something went wrong.'}`),
  });

  const options = [
    { value: '', label: 'Everybody', description: 'The default for this ' + LABEL[kind] },
    ...(people ?? []).map((p) => ({ value: p.id, label: p.displayName, description: undefined })),
  ];

  return (
    <Dialog open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Rates and budget for {name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isPending && <Skeleton className="h-24 w-full" />}

          {rows && rows.length > 0 && (
            <div className="divide-y rounded-md border">
              {rows.map((row) => (
                <div key={row.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                  <span className="font-medium">
                    {row.employee
                      ? (row.employee.alias ?? row.employee.displayName)
                      : 'Everybody'}
                  </span>
                  <span className="ts-num">
                    {currency} {toDecimal(row.rateMinor)}
                  </span>
                  <Badge variant="secondary">{row.isFixed ? 'fixed' : 'per hour'}</Badge>
                  {row.internalRateMinor && (
                    <span className="text-xs text-muted-foreground">
                      costs {currency} {toDecimal(row.internalRateMinor)}
                    </span>
                  )}
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-auto"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(row.id)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {rows && rows.length === 0 && (
            <p className="rounded-md border px-4 py-6 text-center text-sm text-muted-foreground">
              No rate set. Entries fall back to the {kind === 'activities' ? 'project, then the customer, then' : kind === 'projects' ? 'customer, then' : ''} the person&rsquo;s own default.
            </p>
          )}

          {canEdit && (
            <form
              className="space-y-3 rounded-md border p-4"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                if (!/^\d{1,12}(\.\d{1,2})?$/.test(rate.trim())) {
                  return setError('Rate: enter an amount like 1500 or 1500.50.');
                }
                save.mutate({
                  employeeId: employeeId || null,
                  rate: rate.trim(),
                  internalRate: internalRate.trim() || null,
                  isFixed,
                });
              }}
            >
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label>Applies to</Label>
                  <div className="w-52">
                    <Combobox
                      value={employeeId}
                      onChange={setEmployeeId}
                      options={options}
                      placeholder="Everybody"
                      searchPlaceholder="Search people"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rate-amount">Rate ({currency})</Label>
                  <Input
                    id="rate-amount"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    placeholder="1500"
                    className="w-32"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rate-internal">Internal cost</Label>
                  <Input
                    id="rate-internal"
                    value={internalRate}
                    onChange={(e) => setInternalRate(e.target.value)}
                    placeholder="optional"
                    className="w-32"
                  />
                </div>
                <Button hint={"Charge this rate from the chosen date — earlier entries keep theirs"} type="submit" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Set rate'}
                </Button>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={isFixed} onCheckedChange={(v) => setIsFixed(v === true)} />
                Fixed price for the whole entry, not per hour
              </label>
            </form>
          )}

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          {canEdit && (
            <form
              className="space-y-3 rounded-md border p-4"
              onSubmit={(e) => {
                e.preventDefault();
                setBudgetSaved(false);
                setError(null);
                const amount = budgetAmount.trim() || '0';
                if (!/^\d{1,12}(\.\d{1,2})?$/.test(amount)) {
                  return setError('Budget: enter an amount like 250000, or leave it empty for none.');
                }
                const hours = budgetHours.trim() === '' ? 0 : Number(budgetHours);
                if (!Number.isFinite(hours) || hours < 0) {
                  return setError('Budget: time must be in whole or decimal hours, like 40 or 37.5.');
                }
                saveBudget.mutate({
                  budget: amount,
                  timeBudgetHours: hours,
                  budgetType: budgetMonthly ? 'month' : 'total',
                });
              }}
            >
              <p className="text-sm font-medium">Budget</p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="budget-amount">Money ({currency})</Label>
                  <Input
                    id="budget-amount"
                    value={budgetAmount}
                    onChange={(e) => setBudgetAmount(e.target.value)}
                    placeholder="none"
                    className="w-36"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="budget-hours">Time (hours)</Label>
                  <Input
                    id="budget-hours"
                    value={budgetHours}
                    onChange={(e) => setBudgetHours(e.target.value)}
                    placeholder="none"
                    className="w-28"
                  />
                </div>
                <Button hint={"Save the budget"} type="submit" variant="outline" disabled={saveBudget.isPending}>
                  {saveBudget.isPending ? 'Saving…' : budgetSaved ? 'Saved' : 'Save budget'}
                </Button>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={budgetMonthly}
                  onCheckedChange={(v) => setBudgetMonthly(v === true)}
                />
                Resets each month, rather than being a total for all time
              </label>
              <p className="text-xs text-muted-foreground">
                Leave both empty for no budget. A budget only refuses an entry if
                &ldquo;prevent overbooking&rdquo; is switched on at Administration &rarr;
                System &mdash; otherwise it is advisory, which is how the old system ran.
              </p>
            </form>
          )}

          {/* Worth stating, because it is the one thing about rates that surprises
              people: specificity of the THING beats specificity of the PERSON. */}
          <p className="text-xs leading-relaxed text-muted-foreground">
            When several rates could apply, the most specific <em>thing</em> wins first:
            activity, then project, then customer. Within each of those, a rate set for one
            person beats the one set for everybody. So a rate on an activity for everybody
            still outranks a rate on a project for one person. Changing a rate here prices
            future entries only &mdash; what has already been recorded keeps the rate it was
            saved with.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
